import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useUpdateKioskDefaultScreenMutation } from '@occc/graphql';

import { DefaultScreensEditPage } from './default-screens-edit-page.component';

import { DefaultScreenFormValues } from '../../touchpoint.types';
import { routeCreator } from '../../touchpoint.router';
import { useGetDefaultScreen } from '../../hooks/use-get-default-screen.hook';
import { useGetKiosks } from '../../hooks/use-get-kiosks.hook';
import {
  ALL_IMAGES_REQUIRED_MESSAGE,
  DEFAULT_SCREEN_UPDATE_SUCCESS_MESSAGE,
  DEFAULT_SCREEN_UPDATE_ERROR_MESSAGE,
} from '../../touchpoint.constants';

export const DefaultScreensEditPageContainer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { loading, error, defaultScreen } = useGetDefaultScreen({ id: id || '' });
  const [updateKioskDefaultScreen] = useUpdateKioskDefaultScreenMutation();

  const { data: kiosks } = useGetKiosks({
    filter: {
      defaultScreenUuidIncludes: id ? [id] : undefined,
    },
    pageSize: 1000,
    baseOptions: {
      skip: !id,
    },
  });

  const kioskIds = useMemo(() => {
    return kiosks?.map((kiosk) => kiosk.id) || [];
  }, [kiosks]);

  const initialValues: Partial<DefaultScreenFormValues> = useMemo(() => {
    if (!defaultScreen) {
      return {
        name: '',
        description: '',
        landscapeImage: null,
        squareImage: null,
        portraitImage: null,
        landscapeImageUrl: null,
        squareImageUrl: null,
        portraitImageUrl: null,
        kioskIds,
      };
    }

    return {
      name: defaultScreen.name || '',
      description: defaultScreen.description || '',
      landscapeImage: null,
      squareImage: null,
      portraitImage: null,
      landscapeImageUrl: defaultScreen.imageSet?.landscape || null,
      squareImageUrl: defaultScreen.imageSet?.square || null,
      portraitImageUrl: defaultScreen.imageSet?.portrait || null,
      kioskIds,
    };
  }, [defaultScreen, kioskIds]);

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.defaultScreensList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.defaultScreensUpdate(id || ''),
        label: 'Edit',
      },
    ],
    [id],
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: DefaultScreenFormValues) => {
      if (!id) return;

      setIsSubmitting(true);

      try {
        // A partial imageSet is silently ignored by the API, so a removed image would
        // reappear on the record instead of being deleted. Require the full set and
        // surface a snackbar, matching the asset edit page. (OCST-28367)
        if (!values.landscapeImageUrl || !values.squareImageUrl || !values.portraitImageUrl) {
          snackbar.error(ALL_IMAGES_REQUIRED_MESSAGE);
          setIsSubmitting(false);
          return;
        }

        const imageSet = {
          landscape: values.landscapeImageUrl,
          square: values.squareImageUrl,
          portrait: values.portraitImageUrl,
        };

        const { data } = await updateKioskDefaultScreen({
          variables: {
            input: {
              id,
              name: values.name.trim(),
              description: values.description.trim() || undefined,
              imageSet,
              kioskIds: values.kioskIds || [],
            },
          },
          refetchQueries: ['kioskDefaultScreens', 'kioskDefaultScreen', 'kiosks'],
        });

        if (data?.updateKioskDefaultScreen) {
          snackbar.success(DEFAULT_SCREEN_UPDATE_SUCCESS_MESSAGE);
          navigate(routeCreator.defaultScreensList());
        } else {
          snackbar.error(DEFAULT_SCREEN_UPDATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error updating default screen:', error);
        snackbar.error(DEFAULT_SCREEN_UPDATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [updateKioskDefaultScreen, id, navigate],
  );

  return (
    <DefaultScreensEditPage
      loading={loading}
      error={error}
      defaultScreen={defaultScreen}
      breadcrumbLinks={breadcrumbLinks}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      id={id}
    />
  );
};
